import { useAuth0 } from "@auth0/auth0-react";
import { ApiResponse } from "../api/deal-builder-api.ts";
import { RatingAssumption, SaleMethod, SecurityType, TaxDesignation, USState } from "../services/utils/primary-constants.ts";
import useDealBuilderSimilarDealsStore, { SimilarDeal } from "../../stores/deal-builder-similar-deals.store.ts";
import Result from "../utils/result.utils.ts";
import SplineApi from "../../tools/spline-api.js";
import moment from "moment";
import { DealBuilder } from "../services/deal-builder.ts";

type DealBuilderConfig = {
    parAmount: number,
    marketConditionsDate: string,
    callDate: string,
    bondInsurance: boolean,
    saleMethod: SaleMethod,
    stateEnhancement: boolean,
    stateOfIssuance: USState,
    securityType: SecurityType,
    ratingAssumption: RatingAssumption,
    taxDesignation: TaxDesignation,
    couponUserInput: boolean,
    modelVersion: string
}

export type SimilarDealsConfig = Pick<
    DealBuilderConfig,
    "stateOfIssuance" | "stateEnhancement" | "ratingAssumption" |
    "securityType" | "parAmount" | "marketConditionsDate" | "taxDesignation" |
    "saleMethod"
>;

const DEFAULT_SELECTED_DEALS = 3;

export default function useSimilarDealsAPI() {
  const { getAccessTokenSilently } = useAuth0();
  const { similarDeals, setSimilarDeals, setLoading, selectedSimilarDealIds, setSelectedSimilarDealIds, starredSimilarDealCusips, setStarredSimilarDealCusips } = useDealBuilderSimilarDealsStore();
  
  /**
   * Fetches yield predictions from the backend API.
   */
  async function fetchSimilarDeals(inputs: SimilarDealsConfig | { cusip: string } | { cusips: string[] }, opts?: { userDefined?: boolean, starred?: boolean }): Promise<Result<SimilarDeal[]>> {
    const token = await getAccessTokenSilently();
    const splineApi = new SplineApi(token);
    const response: ApiResponse<SimilarDeal[]> = await splineApi.getSimilarDeals(inputs);
    if ("error" in response) return { valid: false, error: response.message };
    return {
      valid: true,
      body: response.map(x => ({
        ...x,
        user_defined: opts?.userDefined ?? false,
        starred: opts?.starred ?? false,
        starred_from_db: opts?.starred ?? false,
      }))
    };
  }

  /**
   * Update the similar deals for a new deal builder
   */
  async function updateSimilarDeals(builder: DealBuilder, savedStarredCusips?: string[]): Promise<Result<SimilarDeal[]>> {
    setLoading(true);

    // Fetch recommended similar deals
    const result: Result<SimilarDeal[]> = await fetchSimilarDeals({
      marketConditionsDate: moment(builder.marketConditionsDate).toISOString(),
      parAmount: builder.totalTargetParAmount,
      ratingAssumption: builder.ratingAssumption,
      securityType: builder.securityType,
      stateEnhancement: builder.stateEnhancement !== undefined,
      stateOfIssuance: builder.stateOfIssuance,
      taxDesignation: builder.taxDesignation,
      saleMethod: builder.saleMethod,
    });

    if (!result.valid) return result;

    // We want to keep the deals the user introduced manually
    const manualDeals = similarDeals.filter(x => x.user_defined);
    const manualIssueIds = new Set(manualDeals.map(x => x.issue_id));

    // Re-fetch starred deals if provided (loading from saved deal)
    const cusipsToStar = savedStarredCusips ?? starredSimilarDealCusips;
    let starredDeals: SimilarDeal[] = [];
    if (cusipsToStar.length > 0) {
      const starredResult = await fetchSimilarDeals({ cusips: cusipsToStar }, { starred: true });
      if (starredResult.valid) {
        starredDeals = starredResult.body.map(x => ({
          ...x,
          user_defined: manualIssueIds.has(x.issue_id),
        }));
        setStarredSimilarDealCusips(cusipsToStar);
      }
    }
    // Starred deals come first, then manual, then recommended (dedup by issue_id)
    const seen = new Set<number>();
    const newSimilarDeals = [...starredDeals, ...manualDeals, ...result.body].filter(x => {
      if (seen.has(x.issue_id)) return false;
      seen.add(x.issue_id);
      return true;
    });
    setSimilarDeals(newSimilarDeals);

    const selectedDeals: number[] = [];
    if (starredDeals.length > 0) {
      // If there are starred deals, select only those
      for (const deal of starredDeals) {
        selectedDeals.push(deal.issue_id);
      }
    } else {
      // Otherwise preserve manual selections and fill up to DEFAULT_SELECTED_DEALS with recommended
      for (const deal of manualDeals) {
        if (selectedSimilarDealIds.includes(deal.issue_id)) {
          selectedDeals.push(deal.issue_id);
        }
      }
      for (const deal of result.body) {
        if (selectedDeals.length === DEFAULT_SELECTED_DEALS) break;
        selectedDeals.push(deal.issue_id);
      }
    }

    setSelectedSimilarDealIds(selectedDeals);
    setLoading(false);

    return result;
  }

  /**
   * Load starred deals from saved cusips and prepend them to the list.
   * Called when opening a saved deal.
   */
  async function loadStarredDeals(cusips: string[]): Promise<void> {
    if (cusips.length === 0) return;
    setLoading(true);
    const result = await fetchSimilarDeals({ cusips }, { starred: true });
    setLoading(false);
    if (!result.valid) return;
    setStarredSimilarDealCusips(cusips);
    // Prepend starred deals (dedup against existing list)
    const existingIds = new Set(similarDeals.map(x => x.issue_id));
    const newDeals = result.body.filter(x => !existingIds.has(x.issue_id));
    setSimilarDeals([ ...newDeals, ...similarDeals ]);
  }

  async function addUserSimilarDeal(cusip: string): Promise<Result<undefined>> {
    if (similarDeals.map(x => x.cusip).flat().some(x => x === cusip)) {
      return { valid: false, error: "The deal is already loaded." }
    }

    setLoading(true);
    const result: Result<SimilarDeal[]> = await fetchSimilarDeals({ cusip }, { userDefined: true, starred: true });
    setLoading(false);
    if (!result.valid) return result;
    if (result.body.length === 0) return { valid: false, error: "Deal not found." }
    const newCusips = result.body.map(x => x.cusip[0]).filter(Boolean);
    setStarredSimilarDealCusips([ ...starredSimilarDealCusips, ...newCusips ]);
    setSimilarDeals([ ...result.body, ...similarDeals ]);
    setSelectedSimilarDealIds([ ...result.body.map(x => x.issue_id), ...selectedSimilarDealIds ]);

    return result;
  }

  return { updateSimilarDeals, addUserSimilarDeal, loadStarredDeals };
}
